import { useState, useRef, useEffect } from "react";
import { Button } from "react-bootstrap";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css"; // Ensure Cropper's CSS is imported
import { set } from "lodash";

export default function ThumbnailSelector({ videoUrl, onSelect }) {
  const [frames, setFrames] = useState([]);
  const [selectedFrame, setSelectedFrame] = useState(null); // Use `null` initially
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cropperRef = useRef(null);

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Failed to get canvas context");
      }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const frameData = canvas.toDataURL("image/jpeg");
      setFrames((prev) => [...prev, frameData]);
      setSelectedFrame(frameData); // Automatically select the newly captured frame
      const { width, height } = video.getBoundingClientRect();
      if (video.videoHeight > video.videoWidth) {
        setImageDimensions({
          width: video?.videoWidth / 4,
          height: video?.videoHeight / 4,
        });
      } else {
        setImageDimensions({ width: width, height: height });
      }
    } catch (err) {
      setError("Error capturing frame: " + err.message);
    }
  };

  const cropImage = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const croppedData = cropper.getCroppedCanvas().toDataURL("image/jpeg");
      onSelect(croppedData); // Pass the cropped image to the parent component
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setError(null);
    setIsLoading(true);

    const handleError = (e) => {
      if (e.message) {
        setError("Error loading video: " + e.message);
      }
      setIsLoading(false);
    };

    const handleLoadedData = () => {
      setIsLoading(false);
    };

    video.onerror = handleError;
    video.onloadeddata = handleLoadedData;
    video.crossOrigin = "anonymous";
    video.src = videoUrl;

    return () => {
      video.onerror = null;
      video.onloadeddata = null;
    };
  }, [videoUrl]);

  const removeFrame = (indexToRemove) => {
    setFrames((prev) => prev.filter((_, index) => index !== indexToRemove));
    if (selectedFrame === frames[indexToRemove]) {
      setSelectedFrame(null); // Reset selection if the removed frame was selected
    }
  };

  return (
    <div className="mt-8">
      <h3 className="text-lg font-medium mb-4">Select Thumbnail</h3>

      {isLoading && <div className="text-gray-500 mb-4">Loading video...</div>}
      {error && <div className="text-red-500 mb-4">{error}</div>}

      <div className="mb-4">
        <video
          ref={videoRef}
          crossOrigin="anonymous"
          controls
          className="w-full aspect-video"
          style={{
            width: "90%",
            height: "auto",
            maxHeight: "300px",
            objectFit: "contain",
          }}
        />
      </div>

      <Button
        variant="secondary"
        className="mb-4 w-full"
        onClick={captureFrame}
        disabled={frames.length >= 1}
      >
        Capture Current Frame
      </Button>

      {selectedFrame && (
        <div className="mb-4">
          <Cropper
            src={selectedFrame}
            style={{
              width: `${imageDimensions.width}px`,
              height: `${imageDimensions.height}px`,
            }}
            initialAspectRatio={16 / 9}
            aspectRatio={1}
            guides={true}
            zoomable={false}
            scalable={false}
            ref={cropperRef}
          />
          <Button variant="primary" className="mt-4 w-full" onClick={cropImage}>
            Crop and Use Selected Thumbnail
          </Button>
        </div>
      )}

      <canvas
        ref={canvasRef}
        style={{
          display: "none",
          position: "absolute",
          visibility: "hidden",
          width: 0,
          height: 0,
        }}
      />

      {/* <div className="grid grid-cols-3 gap-4">
        {frames.map((frame, index) => (
          <div
            key={index}
            style={{
              position: "relative",
              cursor: "pointer",
              borderRadius: "0.5rem",
              overflow: "hidden",
              ...(selectedFrame === frame && {
                border: "2px solid var(--primary)",
                boxShadow: "0 0 0 2px var(--primary)",
              }),
            }}
          >
            <img
              src={frame}
              alt={`Frame ${index + 1}`}
              style={{
                width: "90%",
                height: "auto",
                maxHeight: "300px",
                objectFit: "contain",
                aspectRatio: "16/9",
              }}
              onClick={() => setSelectedFrame(frame)}
            />
            <button
              style={{
                position: "absolute",
                top: "0.5rem",
                right: "0.5rem",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
                borderRadius: "9999px",
                padding: "0.25rem",
                transition: "background-color 0.2s",
                cursor: "pointer",
                border: "none",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
              }}
              onMouseOver={(e) =>
                (e.target.style.backgroundColor = "rgba(0, 0, 0, 0.7)")
              }
              onMouseOut={(e) =>
                (e.target.style.backgroundColor = "rgba(0, 0, 0, 0.5)")
              }
              onClick={(e) => {
                e.stopPropagation();
                removeFrame(index);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                style={{ height: "1rem", width: "1rem" }}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        ))}
      </div> */}
    </div>
  );
}
